import { useMemo } from 'react';
import { JsonSchema } from '@sagold/react-json-editor';

import { useSelections } from '../../../store/hooks/useSelections';

export const useGetUserFilteredData = (
  jsonSchema: JsonSchema,
  tableFilterInstanceId: string
) => {
  const appliedFilters = useSelections(tableFilterInstanceId);

  const defaultsFromSchema = useMemo(() => {
    const result: Record<string, unknown> = {};
    if (!jsonSchema.properties) {
      return result;
    }
    for (const [key, prop] of Object.entries(jsonSchema.properties)) {
      if (typeof prop !== 'object' || prop === null) {
        continue;
      }
      const def = prop.options?.documentUploadDefaultValue;
      if (def != null) {
        result[key] = def;
      }
    }
    return result;
  }, [jsonSchema]);

  // Generate the key-value map based on jsonSchema properties
  const keyValueMap = useMemo(() => {
    if (!jsonSchema?.properties) {
      return {};
    }

    return Object.fromEntries(
      Object.entries(jsonSchema.properties)
        .map(([key, value]) => {
          if (
            value &&
            typeof value !== 'boolean' &&
            'conditionalFilter' in value
          ) {
            return [
              value.conditionalFilter?.dataProperty,
              { key, maxItems: value.maxItems },
            ];
          }
          return [undefined, undefined];
        })
        .filter(([dataProperty]) => dataProperty !== undefined)
    );
  }, [jsonSchema]);

  const updatedObj = useMemo(() => {
    if (!appliedFilters || !keyValueMap) {
      return {};
    }

    return Object.fromEntries(
      Object.entries(appliedFilters)
        .map(([key, value]) => {
          // We need to pre-fill the fields that are part of the keyValueMap
          // So filter out unwated filters set by the user
          if (key in keyValueMap) {
            const { key: newKey, maxItems } = keyValueMap[key];
            if (Array.isArray(value)) {
              return [
                newKey,
                maxItems != null ? value.slice(0, maxItems) : value,
              ];
            }
          }
          return [undefined, undefined];
        })
        .filter(([newKey]) => newKey !== undefined)
    );
  }, [appliedFilters, keyValueMap]);

  return useMemo(
    () => ({ ...defaultsFromSchema, ...updatedObj }),
    [defaultsFromSchema, updatedObj]
  );
};
